import { useEffect, useState } from "react";
import { organizationsAPI } from "../../../services/apiService";
import { DAY, ymd } from "../../lib/format";
import { adAccountObject, apiGet, campaignTotals, objectiveOf, statusOf } from "../../lib/ads";
import { getJson, previousStart, rangeStart, toAppointment } from "../../lib/home";
import useFeatureFlags from "../../../hooks/useFeatureFlags";
import { usePlan } from "../../../context/PlanContext";

// Marketing endpoints answer 403 MODULE_NOT_IN_PLAN for a plan without Marketing;
// Home skips them then instead of asking and failing on every load. While the
// plan is still loading this reads true (the gate fails open, as PlanContext does).
const useMarketingInPlan = () => usePlan()?.hasModule?.("marketing_suite") !== false;

// Data for the Home dashboard. Every number comes from an existing endpoint;
// a call that fails leaves its value null, which the screen shows as "—".
// Items 131–139: collections, plan acceptance, patients by source, booked and
// visited leads, response times, Olli-resolved social chats, Instagram
// interactions (138, exchange rates, is in Home.js).

const facilityQuery = (facilityId) => (facilityId ? `&facility_id=${facilityId}` : "");
const list = (body, ...keys) => {
  if (!body?.success) return null;
  for (const key of keys) {
    const value = key.split(".").reduce((obj, k) => obj?.[k], body);
    if (Array.isArray(value)) return value;
  }
  return null;
};

/** /api/dashboard/stats (counts, wallet, live activity feed), refreshed every minute. */
export function useLiveStats(facilityId) {
  const [state, setState] = useState({ stats: null, loadedAt: null, failed: false });
  useEffect(() => {
    let alive = true;
    const load = () => getJson(`/api/dashboard/stats${facilityId ? `?facility_id=${facilityId}` : ""}`).then((body) => {
      if (!alive) return;
      setState((prev) => (body?.success ? { stats: body.stats, loadedAt: Date.now(), failed: false } : { ...prev, failed: true }));
    });
    load();
    const timer = window.setInterval(load, 60_000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [facilityId]);
  return state;
}

/**
 * Loads one group of dashboard calls once `enabled` turns on, and again when
 * `deps` change. Returns null until the first load finishes; after that the
 * last result stays on screen while a refresh runs. Leaving (or a dev-mode
 * StrictMode re-run) aborts the calls in flight.
 */
function useGroup(enabled, deps, load) {
  const [value, setValue] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    load(ctrl.signal).then((next) => { if (!ctrl.signal.aborted) setValue(next); });
    return () => ctrl.abort();
  }, [enabled, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return value;
}

/**
 * Which call groups each dashboard tab needs on top of "core" (the header's
 * attention list, Overview and Appointments). A tab's groups load the first
 * time it is opened, so the dashboard opens on ~20 calls instead of ~45.
 */
export const TAB_GROUPS = {
  overview: [],
  appointments: [],
  "patients-revenue": ["revenue"],
  growth: ["growth"],
  conversations: ["conversations"],
  "ai-operations": ["conversations"],
};

/** Everything that does not depend on the date range. `groups`: { revenue, growth, conversations } to load. */
export function useHomeBase(facilityId, groups = {}) {
  const marketing = useMarketingInPlan();
  const core = useGroup(true, [facilityId], async (signal) => {
    const fq = facilityQuery(facilityId);
    const yesterday = ymd(new Date().setHours(0, 0, 0, 0) - DAY);
    const [agents, complaints, pendingTasks, activeTasks, campaigns, kb, schedules, wabaOldestUnread, org] = await Promise.all([
      getJson("/api/ai-agent/config", signal),
      getJson(`/api/complaints?status=open,acknowledged,in_progress,waiting_on_patient,reopened&limit=500${fq}`, signal),
      getJson(`/api/leads/tasks/all?status=pending&due_to=${yesterday}&sort_by=due_date&sort_order=ASC&limit=100`, signal),
      getJson(`/api/leads/tasks/all?status=in_progress&due_to=${yesterday}&sort_by=due_date&sort_order=ASC&limit=100`, signal),
      marketing ? apiGet("/api/ads/campaigns") : null,
      getJson("/api/kb/documents?limit=500", signal),
      getJson(`/api/doctor-schedules?is_active=true${fq}`, signal),
      // The oldest unread chat. Counts for the period come from useHomeRange.
      getJson("/api/waba/conversations?status=open&unread=1&sort=oldest&limit=1", signal),
      organizationsAPI.getMyOrganizations().then((r) => r.data).catch(() => null),
    ]);
    const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch (_) { return {}; } })();
    const orgs = org?.success ? org.data || [] : [];
    const clinic = orgs.find((o) => o.id === user.organization_id) || orgs[0];
    const tasks = [list(pendingTasks, "data"), list(activeTasks, "data")];
    return {
      clinicName: clinic?.name || "",
      userName: user.name || "",
      agents: list(agents, "configs"),
      complaints: list(complaints, "data.complaints"),
      overdueTasks: tasks.every(Boolean) ? [...tasks[0], ...tasks[1]].sort((a, b) => String(a.due_date).localeCompare(String(b.due_date))) : null,
      overdueTotal: tasks.every(Boolean) ? Number(pendingTasks.pagination?.total || 0) + Number(activeTasks.pagination?.total || 0) : null,
      campaigns: campaigns?.success ? campaigns.data?.cached || [] : null,
      kbDocs: list(kb, "data"),
      schedules: list(schedules, "data"),
      wabaOldestUnread: list(wabaOldestUnread, "conversations")?.[0] ?? null,
      wabaLoaded: !!wabaOldestUnread?.success,
    };
  });

  const conversations = useGroup(!!groups.conversations, [facilityId], async (signal) => {
    const [wabaWaiting, social, socialStats] = await Promise.all([
      // The five oldest chats waiting on the clinic.
      getJson("/api/waba/conversations?status=open&waiting=1&sort=oldest&limit=5", signal),
      marketing ? getJson("/api/social-inbox?limit=300", signal) : null,
      marketing ? getJson("/api/social-inbox/stats", signal) : null,
    ]);
    return { wabaWaiting: list(wabaWaiting, "conversations"), social: list(social, "data"), socialStats: socialStats?.success ? socialStats.data : null };
  });

  const revenue = useGroup(!!groups.revenue, [facilityId], async (signal) => {
    const txns = await getJson("/api/waba/wallet/transactions?limit=100", signal);
    return { transactions: list(txns, "transactions") };
  });

  const growth = useGroup(!!groups.growth, [facilityId], async (signal) => {
    const [integrations, adsStatus] = await Promise.all([
      getJson("/api/integrations", signal),
      getJson("/api/integrations/ads/ads-status", signal),
    ]);
    const meta = (integrations?.data || []).filter((i) => ["meta_leads", "meta_facebook", "meta_instagram"].includes(i.integration_type) && i.is_connected && i.page_id);
    const igIntegration = meta.find((i) => i.instagram_business_id) || null;
    const igAccount = igIntegration ? await getJson(`/api/instagram/account?integrationId=${igIntegration.id}`, signal) : null;
    return {
      adAccountId: adsStatus?.data?.ad_account_id || adsStatus?.ad_account_id || null,
      igIntegrationId: igIntegration?.id || null,
      igFollowers: igAccount?.success ? igAccount.data?.followers_count ?? null : null,
      hasInstagram: !!igIntegration,
    };
  });

  return {
    ...core, ...conversations, ...revenue, ...growth,
    loaded: !!core,
    ready: { conversations: !!conversations, revenue: !!revenue, growth: !!growth },
  };
}

const PRESET = { today: "today", "7d": "last_7d", "30d": "last_30d", month: "this_month" };

/** Everything for the chosen range (and the period before it, for comparisons). `groups` as for useHomeBase. */
export function useHomeRange(range, facilityId, now, base, groups = {}) {
  const marketing = useMarketingInPlan();
  const fq = facilityQuery(facilityId);
  const from = ymd(rangeStart(range, now));
  const periodSpan = `date_from=${from}&date_to=${ymd(now)}`;
  const deps = [range, facilityId, now];
  const sumTotals = (body) => {
    if (!body?.success) return null;
    return Object.entries(body.data?.cells || {}).filter(([k]) => k.endsWith("||_total")).reduce((n, [, c]) => n + (Number(c.value ?? c.count) || 0), 0);
  };

  // Overview, Appointments and the header.
  const core = useGroup(true, deps, async (signal) => {
    const today = new Date(now).setHours(0, 0, 0, 0);
    const prevFrom = previousStart(range, now);
    const futureEnd = range === "today" ? today + DAY : range === "7d" ? today + 7 * DAY : range === "30d" ? today + 30 * DAY : new Date(new Date(now).getFullYear(), new Date(now).getMonth() + 1, 1).getTime();
    const span = `date_from=${ymd(prevFrom)}&date_to=${ymd(now)}`;
    const prevSpan = `date_from=${ymd(prevFrom)}&date_to=${ymd(rangeStart(range, now) - DAY)}`;
    const [appts, patients, leadDaily, byStatus, statuses, collections, collectionsPrev, booked, visited, responseTimes] = await Promise.all([
      getJson(`/api/appointments?date_from=${ymd(prevFrom)}&date_to=${ymd(futureEnd - 1)}&limit=3000${fq}`, signal),
      getJson(`/api/patients?${span}&limit=3000${fq}`, signal),
      getJson(`/api/lead-analytics/pivot?row=created_day&col=source&measure=count&${span}${fq}`, signal),
      getJson(`/api/lead-analytics/pivot?row=status&measure=count&${periodSpan}${fq}`, signal),
      getJson("/api/lead-statuses", signal),
      // Collections (131): by day, and the period before.
      getJson(`/api/collections?${periodSpan}&group=day${fq}`, signal),
      getJson(`/api/collections?${prevSpan}&group=day${fq}`, signal),
      // Leads that got a booking, and that came in (134), per source.
      getJson(`/api/lead-analytics/pivot?row=source&measure=appointment_booked&${periodSpan}${fq}`, signal),
      getJson(`/api/lead-analytics/pivot?row=source&measure=visited&${periodSpan}${fq}`, signal),
      getJson(`/api/waba/conversations/response-times?${periodSpan}&bucket=${range === "today" ? "hour" : "day"}`, signal),
    ]);
    return {
      appointments: list(appts, "data")?.map(toAppointment).filter((a) => a.start !== null) ?? null,
      patients: list(patients, "data"),
      leadCells: leadDaily?.success ? leadDaily.data?.cells || {} : null,
      statusCells: byStatus?.success ? byStatus.data?.cells || {} : null,
      leadStatuses: list(statuses, "data"),
      collections: collections?.success ? { total: Number(collections.total) || 0, byDay: Object.fromEntries((collections.rows || []).map((r) => [r.key, Number(r.amount_inr) || 0])) } : null,
      collectionsPrev: collectionsPrev?.success ? Number(collectionsPrev.total) || 0 : null,
      booked: sumTotals(booked),
      visited: sumTotals(visited),
      responseTimes: responseTimes?.success ? { avg: responseTimes.avg_minutes ?? null, median: responseTimes.median_minutes ?? null, series: responseTimes.series || [] } : null,
    };
  });

  // Patients & revenue.
  const revenue = useGroup(!!groups.revenue, deps, async (signal) => {
    const [plans, collectionsByTreatment, planStats, patientStats] = await Promise.all([
      getJson(`/api/patient-treatments?${periodSpan}&limit=1000${fq}`, signal),
      getJson(`/api/collections?${periodSpan}&group=treatment${fq}`, signal),
      getJson(`/api/patient-treatments/stats?${periodSpan}${fq}`, signal),
      getJson(`/api/patients/stats?${periodSpan}${fq}`, signal),
    ]);
    return {
      plans: list(plans, "data"),
      collectionsByTreatment: collectionsByTreatment?.success ? (collectionsByTreatment.rows || []).map((r) => [r.key, Number(r.amount_inr) || 0]) : null,
      planAcceptance: planStats?.success && planStats.data?.accepted ? { accepted: planStats.data.accepted, pending: planStats.data.pending, declined: planStats.data.declined, rate: planStats.data.acceptance_rate ?? null } : null,
      patientsBySource: patientStats?.success ? patientStats.data?.by_source || {} : null,
    };
  });

  // Growth.
  const growth = useGroup(!!groups.growth, deps, async (signal) => {
    const [wonBySource, leadHours] = await Promise.all([
      getJson(`/api/lead-analytics/pivot?row=source&measure=won&${periodSpan}${fq}`, signal),
      range === "today" ? getJson(`/api/lead-analytics/pivot?row=hour_of_day&col=source&measure=count&date_from=${ymd(now)}&date_to=${ymd(now)}${fq}`, signal) : null,
    ]);
    return {
      wonCells: wonBySource?.success ? wonBySource.data?.cells || {} : null,
      leadHourCells: leadHours?.success ? leadHours.data?.cells || {} : null,
    };
  });

  // Conversations, AI & operations.
  const conversations = useGroup(!!groups.conversations, deps, async (signal) => {
    const [aiConvs, wabaPeriod, socialStats] = await Promise.all([
      // Counts for the period, counted on the server (limit=1: only the counts
      // are read). Both org-wide: AI conversations carry no location, and the
      // channel chart they feed is labelled "Across all locations" beside the
      // org-wide Instagram and Facebook bars.
      getJson(`/api/ai-agent/conversations?${periodSpan}&limit=1`, signal),
      getJson(`/api/waba/conversations?${periodSpan}&limit=1`, signal),
      marketing ? getJson(`/api/social-inbox/stats?${periodSpan}`, signal) : null,
    ]);
    return {
      aiCounts: aiConvs?.success && aiConvs.counts ? aiConvs.counts : null,
      wabaCounts: wabaPeriod?.success && wabaPeriod.counts ? wabaPeriod.counts : null,
      aiResolvedSocial: socialStats?.success ? { instagram: Number(socialStats.data?.ai_resolved_instagram ?? 0), facebook: Number(socialStats.data?.ai_resolved_messenger ?? 0) } : null,
    };
  });

  // Meta features still in review answer 403 FEATURE_COMING_SOON: their calls
  // wait for the flags and are skipped when off; the cards say "coming soon".
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const adInsightsOn = isEnabled("meta_ad_insights");
  const pagePerfOn = isEnabled("meta_page_performance"); // /api/insights/*
  const igInsightsOn = isEnabled("meta_instagram_insights"); // /api/instagram/insights/*

  // Ad spend for the period and each campaign's numbers (Meta insights).
  const [ads, setAds] = useState({ spend: null, byCampaign: {}, loading: true });
  const campaignIds = (base.campaigns || []).map((c) => c.external_campaign_id).join(",");
  useEffect(() => {
    if (!groups.growth || flagsLoading) return undefined;
    if (!adInsightsOn) { setAds({ spend: null, byCampaign: {}, soon: true }); return undefined; }
    let alive = true;
    const preset = PRESET[range];
    // Active campaigns first; the insights call is one Meta request per campaign.
    const picked = [...(base.campaigns || [])].sort((a, b) => (statusOf(b) === "Active") - (statusOf(a) === "Active")).slice(0, 6);
    (async () => {
      const [account, totals] = await Promise.all([
        base.adAccountId ? apiGet(`/api/ads/insights/${adAccountObject(base.adAccountId)}?preset=${preset}`) : null,
        Promise.all(picked.map((c) => campaignTotals(c.external_campaign_id, objectiveOf(c.objective), preset))),
      ]);
      if (!alive) return;
      const summary = account?.success ? account.data?.summary : null;
      const byCampaign = {};
      picked.forEach((c, i) => { byCampaign[c.external_campaign_id] = totals[i]; });
      setAds({ spend: summary?.spend != null ? Number(summary.spend) : null, byCampaign });
    })();
    return () => { alive = false; };
  }, [groups.growth, flagsLoading, adInsightsOn, range, base.adAccountId, campaignIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facebook Page and Instagram audience for the period.
  const [social, setSocial] = useState({ fb: null, igReach: null, igInteractions: null, loading: true });
  useEffect(() => {
    if (!groups.growth || flagsLoading) return undefined;
    let alive = true;
    const from = rangeStart(range, now);
    const days = Math.max(1, Math.round((now - from) / DAY));
    (async () => {
      const [overview, igInsights, igPerf] = await Promise.all([
        pagePerfOn ? getJson(`/api/insights/overview?days=${days}`) : null,
        base.igIntegrationId && igInsightsOn ? getJson(`/api/instagram/insights/account?integrationId=${base.igIntegrationId}&metric=reach&period=day&since=${ymd(from)}&until=${ymd(now)}`) : null,
        // Instagram interactions over the period (item 139), from the stored daily numbers.
        base.igIntegrationId && pagePerfOn ? getJson(`/api/insights/performance?platform=instagram&integrationId=${base.igIntegrationId}&date_from=${ymd(from)}&date_to=${ymd(now)}`) : null,
      ]);
      if (!alive) return;
      const page = overview?.success && overview.data?.connected_pages ? overview.data : null;
      const reach = (igInsights?.success ? igInsights.data || [] : []).find((m) => m.name === "reach");
      setSocial({
        fb: page ? { followers: Math.max(0, ...(page.by_page || []).map((p) => p.followers)) || null, reach: page.totals?.reach ?? null, engagements: page.totals?.engagements ?? null } : null,
        igReach: reach ? (reach.values || []).reduce((n, v) => n + (Number(v.value) || 0), 0) : null,
        igInteractions: igPerf?.success && (igPerf.data?.daily || []).some((d) => d.interactions !== null) ? igPerf.data.daily.reduce((n, d) => n + (Number(d.interactions) || 0), 0) : null,
        // What is still in Meta review, for the card's "coming soon" note.
        soon: { pagePerformance: !pagePerfOn, instagramInsights: !igInsightsOn },
      });
    })();
    return () => { alive = false; };
  }, [groups.growth, flagsLoading, pagePerfOn, igInsightsOn, range, now, base.igIntegrationId]);

  return {
    ...core, ...revenue, ...growth, ...conversations, ads, social,
    loaded: !!core,
    ready: { revenue: !!revenue, growth: !!growth, conversations: !!conversations },
  };
}
